import { type FunctionComponent } from 'react';
import { JsonLd } from 'react-schemaorg';
import { type OpeningHoursSpecification, type Product, type Store } from 'schema-dts';
import { SITE_NAME } from '../../../../constants/general';
import { STRUCTURED_DATA_IMAGE_OPTIONS } from '../../../../constants/images';
import { SITE_PATH } from '../../../../constants/links';
import { generateCloudinaryUrl } from '../../../../helpers/cloudinary-helper/cloudinary-helper';
import { isProductPage, isShowroomLocationPage, type PageTypes } from '../page-container/page-container.types';
import { getSchemaDayOfweek } from './page-structured-data.helper';

export type PageStructuredDataProps = {
	pageTypeProps: PageTypes;
};

export const PageStructuredData: FunctionComponent<PageStructuredDataProps> = ({ pageTypeProps }) => {
	if (isProductPage(pageTypeProps)) {
		return (
			<JsonLd<Product>
				item={{
					'@context': 'https://schema.org',
					'@type': 'Product',
					name: pageTypeProps.productName,
					description: pageTypeProps.productDescription || undefined,
					productID: pageTypeProps.productId,
					image: generateCloudinaryUrl(pageTypeProps.imagePublicId, STRUCTURED_DATA_IMAGE_OPTIONS),
					sku: pageTypeProps.modelNumber,
					brand: {
						'@type': 'Brand',
						name: pageTypeProps.brandName
					},
					offers: {
						'@type': 'Offer',
						'@id': SITE_PATH + pageTypeProps.productUrl,
						priceCurrency: 'USD',
						price: pageTypeProps.price,
						sku: pageTypeProps.modelNumber,
						availability: pageTypeProps.itemAvailability,
						seller: {
							'@type': 'Organization',
							name: SITE_NAME
						},
						url: SITE_PATH + pageTypeProps.productUrl
					},
					...(pageTypeProps.ratingCount &&
						pageTypeProps.ratingValue && {
							aggregateRating: {
								'@type': 'AggregateRating',
								ratingValue: pageTypeProps.ratingValue,
								reviewCount: pageTypeProps.ratingCount,
								worstRating: 1,
								bestRating: 5
							}
						})
				}}
			/>
		);
	}

	if (isShowroomLocationPage(pageTypeProps)) {
		return (
			<JsonLd<Store>
				item={{
					'@context': 'https://schema.org',
					'@type': 'Store',
					image: generateCloudinaryUrl(pageTypeProps.imagePublicId, { crop: 'fill', type: 'fetch' }),
					name: SITE_NAME,
					telephone: pageTypeProps.phoneNumber,
					address: {
						'@type': 'PostalAddress',
						streetAddress: pageTypeProps.address.addressLine1,
						addressLocality: pageTypeProps.address.city,
						addressRegion: pageTypeProps.address.state,
						postalCode: pageTypeProps.address.zipCode,
						addressCountry: 'US'
					},
					openingHoursSpecification: pageTypeProps.hours.map<OpeningHoursSpecification>((dayHour) => ({
						'@type': 'OpeningHoursSpecification',
						dayOfWeek: getSchemaDayOfweek(dayHour.dayName),
						opens: dayHour.hours?.startTime,
						closes: dayHour.hours?.endTime
					}))
				}}
			/>
		);
	}

	return null;
};
